import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alan, Buton, Kart, MetinAlani } from "@koclukpanel/ui";
import {
  CARK_BEKLEME_SAAT_EN_AZ,
  CARK_BEKLEME_SAAT_EN_COK,
  CARK_DILIM_EN_AZ,
  CARK_DILIM_EN_COK,
  GEREKCE_AGIR_EN_AZ,
  GEREKCE_EN_COK,
  type CarkTabloIstegi,
  type CarkYonetimDurumu,
} from "@koclukpanel/contracts";
import { ApiHatasi } from "./api";
import { carkTabloKaydet } from "./api-oyun2";
import {
  dogrula,
  haftalikCevirme,
  onizlemeHesapla,
  ortalamaMetni,
  tamSayiOku,
  yuksekDegisiklikMi,
  type DilimGirdisi,
} from "./cark-onizleme";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * ÇARK TABLOSUNU DÜZENLE — F-GAM-19, sürüm 7b kararlar (`oyun.cark.yonet`, sudo).
 *
 * Tablo DEĞİŞMEZ kayıttır: kaydetmek yeni sürüm açar ve HEMEN geçerli olur; eski çevirmeler kendi sürümünde kalır.
 * Ekran yalnız GÖSTERİM için olasılık/nadirlik/beklenen ortalama hesaplar (`cark-onizleme.ts`); doğrulamayı ve kaydı
 * sunucu yapar. `oncekiSurum` formun AÇILDIĞI andaki etkin sürümdür: araya başkası sürüm açtıysa sunucu 409
 * `SURUM_DEGISTI` döner; ekran düzenlemeyi SİLMEZ, uyarır ve güncel hâli yüklemeyi kullanıcıya bırakır.
 *
 * Rol adına bakılmaz: bu bileşen yalnız `yonetebilir` true iken çizilir (üst bileşen karar verir).
 */

function sayi(n: number): string {
  return n.toLocaleString("tr-TR");
}

function satirlarDan(d: CarkYonetimDurumu): DilimGirdisi[] {
  return d.dilimler.map((s, i) => ({
    anahtar: i,
    puan: String(s.puan),
    agirlik: String(s.agirlik),
  }));
}

export function CarkTabloDuzenle({
  durum,
  onKapat,
  onKaydedildi,
  onGuncelYukle,
  guncelYukleniyor,
}: {
  /** Formun açıldığı andaki etkin durum: `oncekiSurum` ve başlangıç değerleri buradan. */
  durum: CarkYonetimDurumu;
  onKapat: () => void;
  onKaydedildi: (yeni: CarkYonetimDurumu) => void;
  /** Güncel etkin tabloyu yükleyip formu onunla yeniden açar (düzenleme kullanıcının isteğiyle atılır). */
  onGuncelYukle: () => void;
  guncelYukleniyor: boolean;
}) {
  const { hataYakala, kapi } = useSudoKapisi(
    "Çark tablosunu kaydetmek için şifrenizi yeniden girin.",
  );
  const [satirlar, setSatirlar] = useState<DilimGirdisi[]>(() => satirlarDan(durum));
  const [bekleme, setBekleme] = useState(String(durum.bekleme));
  const [gerekce, setGerekce] = useState("");
  const [onay, setOnay] = useState(false);
  const sonrakiAnahtar = useRef(durum.dilimler.length);
  const [odakAnahtar, setOdakAnahtar] = useState<number | null>(null);
  const kok = useRef<HTMLFormElement | null>(null);

  const mut = useMutation({
    mutationFn: (g: CarkTabloIstegi) => carkTabloKaydet(g),
    onSuccess: (y) => onKaydedildi(y),
    // Sudo penceresi kapalıysa şifre sorulur; doğrulanınca AYNI gövdeyle istek yinelenir.
    onError: (e, g) => hataYakala(() => mut.mutate(g))(e),
  });

  // Form açılınca ilk alana, satır eklenince yeni satırın puan alanına odak.
  useEffect(() => {
    kok.current?.querySelector("input")?.focus();
  }, []);
  useEffect(() => {
    if (odakAnahtar === null) return;
    kok.current
      ?.querySelector<HTMLInputElement>(`li[data-anahtar="${odakAnahtar}"] input`)
      ?.focus();
    setOdakAnahtar(null);
  }, [odakAnahtar]);

  const sonuc = dogrula({
    satirlar,
    bekleme,
    gerekce,
    oncekiSurum: durum.surum,
    mevcutBekleme: durum.bekleme,
  });
  const onizleme = onizlemeHesapla(satirlar);
  const haftalik = haftalikCevirme(tamSayiOku(bekleme));

  const surumDegisti =
    mut.error instanceof ApiHatasi && mut.error.durum === 409 && mut.error.kod === "SURUM_DEGISTI";
  const sudoSorulacak = mut.error instanceof ApiHatasi && mut.error.sudoGerekliMi;
  const genelHata = mut.isError && !surumDegisti && !sudoSorulacak;
  const kaydedilebilir = sonuc.govde !== null && !surumDegisti;

  /** Her düzenleme onay özetini kapatır: özet her zaman ekrandaki tabloyu anlatsın. */
  function degistir(f: () => void) {
    setOnay(false);
    // 409 uyarısı düzenlemeyle KAYBOLMAZ: eski sürüme dayanan istek yine reddedilir, uyarı güncel hâli yükleyene dek durur.
    if (!surumDegisti) mut.reset();
    f();
  }

  function satirDegistir(anahtar: number, alan: "puan" | "agirlik", deger: string) {
    degistir(() =>
      setSatirlar((s) => s.map((x) => (x.anahtar === anahtar ? { ...x, [alan]: deger } : x))),
    );
  }

  function satirEkle() {
    degistir(() => {
      const anahtar = sonrakiAnahtar.current++;
      setSatirlar((s) => [...s, { anahtar, puan: "", agirlik: "" }]);
      setOdakAnahtar(anahtar);
    });
  }

  function satirSil(anahtar: number) {
    degistir(() => setSatirlar((s) => s.filter((x) => x.anahtar !== anahtar)));
  }

  function puanaGoreSirala() {
    degistir(() =>
      setSatirlar((s) => {
        const sayili = s.filter((x) => tamSayiOku(x.puan) !== null);
        const digerleri = s.filter((x) => tamSayiOku(x.puan) === null);
        sayili.sort((a, b) => (tamSayiOku(a.puan) ?? 0) - (tamSayiOku(b.puan) ?? 0));
        return [...sayili, ...digerleri];
      }),
    );
  }

  const mevcutOrtalama = durum.beklenenDeger;
  const yeniOrtalama = onizleme.beklenen;
  const yuksek = yeniOrtalama !== null && yuksekDegisiklikMi(mevcutOrtalama, yeniOrtalama);
  const degisimYuzdesi =
    yeniOrtalama !== null && mevcutOrtalama > 0
      ? Math.round((Math.abs(yeniOrtalama - mevcutOrtalama) / mevcutOrtalama) * 100)
      : null;

  const kapaliNedeniId = "cark-kaydet-kapali-nedeni";

  return (
    <Kart
      baslik="Tabloyu düzenle"
      aciklama={`Kaydedince Sürüm ${durum.surum + 1} açılır ve hemen geçerli olur; eski çevirmeler etkilenmez. Önizleme yalnız gösterimdir, doğrulamayı sunucu yapar.`}
    >
      <form
        ref={kok}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (kaydedilebilir) setOnay(true);
        }}
      >
        <ol className="flex flex-col gap-3" aria-label="Çark dilimleri">
          {satirlar.map((s, i) => {
            const hata = sonuc.satirlar[i] ?? {};
            const on = onizleme.satirlar[i] ?? null;
            const agirlik = tamSayiOku(s.agirlik);
            return (
              <li
                key={s.anahtar}
                data-anahtar={s.anahtar}
                className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <Alan
                      etiket={`${i + 1}. dilim puanı`}
                      inputMode="numeric"
                      autoComplete="off"
                      value={s.puan}
                      onChange={(e) => satirDegistir(s.anahtar, "puan", e.target.value)}
                      {...(hata.puan ? { hata: hata.puan } : {})}
                    />
                  </div>
                  <div className="min-w-0">
                    <Alan
                      etiket={`${i + 1}. dilim ağırlığı`}
                      inputMode="numeric"
                      autoComplete="off"
                      value={s.agirlik}
                      onChange={(e) => satirDegistir(s.anahtar, "agirlik", e.target.value)}
                      {...(hata.agirlik ? { hata: hata.agirlik } : {})}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-notr-700 text-sm tabular-nums">
                    {agirlik === 0
                      ? "Önizleme: bu dilim hiç çıkmaz (%0)."
                      : on
                        ? `Önizleme: olasılık ${on.yuzde} · nadirlik ${on.nadirlik}`
                        : "Önizleme için geçerli bir ağırlık girin."}
                  </p>
                  <Buton
                    type="button"
                    gorunum="sessiz"
                    disabled={satirlar.length <= CARK_DILIM_EN_AZ}
                    aria-label={`${i + 1}. dilimi sil`}
                    onClick={() => satirSil(s.anahtar)}
                  >
                    Sil
                  </Buton>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap gap-2">
          <Buton
            type="button"
            gorunum="ikincil"
            disabled={satirlar.length >= CARK_DILIM_EN_COK}
            onClick={satirEkle}
          >
            Dilim ekle
          </Buton>
          <Buton type="button" gorunum="sessiz" onClick={puanaGoreSirala}>
            Puana göre sırala
          </Buton>
          <p className="text-notr-600 self-center text-xs">
            {satirlar.length}/{CARK_DILIM_EN_COK} dilim (en az {CARK_DILIM_EN_AZ}). Puanlar tekil ve
            küçükten büyüğe sıralı olmalı; sıfır ağırlıklı dilim tabloda durur ama hiç çıkmaz.
          </p>
        </div>

        <dl className="bg-notr-50 rounded-alan grid grid-cols-1 gap-2 p-3 text-sm sm:grid-cols-3">
          <OzetSatiri etiket="Toplam ağırlık" deger={sayi(onizleme.toplam)} />
          <OzetSatiri
            etiket="Çevirme başına beklenen ortalama"
            deger={yeniOrtalama === null ? "—" : `≈ ${ortalamaMetni(yeniOrtalama)} puan`}
          />
          <OzetSatiri
            etiket={
              haftalik === null
                ? "Haftalık beklenen"
                : `Haftalık beklenen (en çok ${haftalik} çevirme)`
            }
            deger={
              yeniOrtalama === null || haftalik === null
                ? "—"
                : `≈ ${ortalamaMetni(yeniOrtalama * haftalik)} puan`
            }
          />
        </dl>

        <div className="max-w-xs">
          <Alan
            etiket="Hak aralığı (saat)"
            inputMode="numeric"
            autoComplete="off"
            value={bekleme}
            onChange={(e) => degistir(() => setBekleme(e.target.value))}
            yardim={`Son çevirmeden sonra yeni hakkın kaç saat sonra geleceği (${CARK_BEKLEME_SAAT_EN_AZ}–${CARK_BEKLEME_SAAT_EN_COK}). Şu an ${durum.bekleme} saat; hak birikmez.`}
            {...(sonuc.bekleme ? { hata: sonuc.bekleme } : {})}
          />
        </div>

        <MetinAlani
          etiket="Gerekçe"
          zorunlu
          rows={3}
          maxLength={GEREKCE_EN_COK}
          value={gerekce}
          onChange={(e) => degistir(() => setGerekce(e.target.value))}
          yardim={`Denetim kaydına yazılır, en az ${GEREKCE_AGIR_EN_AZ} karakter. Neden değiştirildiğini yazın.`}
          {...(sonuc.gerekce ? { hata: sonuc.gerekce } : {})}
        />

        {surumDegisti && (
          <div
            role="alert"
            className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 flex flex-col items-start gap-2 border p-3 text-sm"
          >
            <p>Tablo siz açtıktan sonra değişti; güncel hâli yükleyin.</p>
            <p>Düzenlemeniz şimdilik ekranda duruyor; güncel hâli yüklerseniz yerini alır.</p>
            <Buton
              type="button"
              gorunum="ikincil"
              yukleniyor={guncelYukleniyor}
              yukleniyorMetni="Yükleniyor"
              onClick={onGuncelYukle}
            >
              Güncel hâli yükle
            </Buton>
          </div>
        )}

        {genelHata && (
          <p role="alert" className="text-hata-700 text-sm">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Tablo kaydedilemedi."}
          </p>
        )}

        {onay && sonuc.govde && (
          <div
            role="region"
            aria-label="Kayıt onayı"
            className="rounded-alan border-marka-200 bg-marka-50 flex flex-col gap-2 border p-3 text-sm"
          >
            <p className="text-notr-900 font-semibold">
              Yeni sürüm {durum.surum + 1}: beklenen ortalama{" "}
              {yeniOrtalama === null ? "—" : ortalamaMetni(yeniOrtalama)} puan; mevcut ortalama{" "}
              {ortalamaMetni(mevcutOrtalama)} puan.
            </p>
            {yuksek && (
              <p
                role="alert"
                className="rounded-alan border-uyari-200 bg-uyari-50 text-uyari-800 border p-2 font-medium"
              >
                Dikkat: beklenen ortalama
                {degisimYuzdesi === null ? " büyük ölçüde" : ` %${sayi(degisimYuzdesi)}`} değişiyor
                (%50&apos;den fazla). Bu, öğrencilere verilen puan miktarını doğrudan etkiler;
                puanları ve ağırlıkları bir kez daha kontrol edin.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Buton
                type="button"
                yukleniyor={mut.isPending}
                yukleniyorMetni="Kaydediliyor"
                disabled={!kaydedilebilir}
                onClick={() => sonuc.govde && mut.mutate(sonuc.govde)}
              >
                Onayla ve yeni sürümü kaydet
              </Buton>
              <Buton
                type="button"
                gorunum="ikincil"
                disabled={mut.isPending}
                onClick={() => setOnay(false)}
              >
                Geri dön
              </Buton>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {!kaydedilebilir && !surumDegisti && sonuc.genel.length > 0 && (
            <ul id={kapaliNedeniId} className="text-notr-700 list-disc pl-5 text-sm">
              {sonuc.genel.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Buton
              type="submit"
              disabled={!kaydedilebilir || onay}
              aria-describedby={
                !kaydedilebilir && sonuc.genel.length > 0 ? kapaliNedeniId : undefined
              }
            >
              Kaydet
            </Buton>
            <Buton type="button" gorunum="ikincil" onClick={onKapat}>
              Vazgeç
            </Buton>
          </div>
        </div>
      </form>
      {/* Şifre kapısı kendi <form>unu çizer: iç içe form geçersiz HTML, bu yüzden formun DIŞINDA. */}
      {kapi && <div className="mt-4">{kapi}</div>}
    </Kart>
  );
}

function OzetSatiri({ etiket, deger }: { etiket: string; deger: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-notr-600">{etiket}</dt>
      <dd className="text-notr-900 font-semibold tabular-nums">{deger}</dd>
    </div>
  );
}
